<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="900">
        <v-form v-model="formData.valid" class="mb-3" enctype="multipart/form-data" validate-on="input lazy"
                @submit.prevent="submit(false)">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Import Devices</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-autocomplete v-model="formData.product_sku_id" :items="skus" :loading="loadingSkus"
                                            :rules="[v => !!v || 'SKU is required']"
                                            density="compact" item-title="label" item-value="id"
                                            label="Product SKU" variant="outlined"/>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-autocomplete v-model="formData.sim_card_type_id" :items="simCardTypes"
                                            :loading="loadingSimCardTypes"
                                            density="compact" item-title="label" item-value="id"
                                            label="SIM provider (for ICCIDs not yet in DB)"
                                            variant="outlined" clearable
                                            hint="Used to auto-create sim_cards entries when an iccid is new"
                                            persistent-hint/>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-autocomplete v-model="formData.portal_id" :items="portals"
                                            :loading="loadingPortals"
                                            density="compact" item-title="label" item-value="id"
                                            label="Portal (optional)"
                                            variant="outlined" clearable
                                            hint="Imported devices will be assigned to this portal"
                                            persistent-hint/>
                        </v-col>

                        <v-col cols="12">
                            <v-label class="mb-3">Upload File (CSV or XLSX)</v-label>
                            <v-file-input v-model="formData.csv"
                                          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                          label="Select CSV or XLSX file"
                                          @update:modelValue="onFilePicked"/>
                            <div class="mt-1">
                                <small class="text-grey-darken-1">
                                    CSV or XLSX accepted. MACs accept colons or no colons. Whitespace is trimmed automatically.
                                </small>
                            </div>
                        </v-col>

                        <v-col cols="12">
                            <v-checkbox v-model="formData.continue_on_error" density="compact" hide-details
                                        label="Continue if some rows fail (skip invalid, save the rest)"/>
                            <v-checkbox v-model="formData.overwrite_existing" density="compact" hide-details
                                        label="Overwrite existing devices with the same slno"/>
                        </v-col>
                    </v-row>

                    <!-- Profiles bar ---------------------------------------------------------- -->
                    <v-divider class="my-3"/>
                    <v-row dense align="center">
                        <v-col cols="12" sm="6">
                            <v-autocomplete v-model="selectedProfileId" :items="profileItems" :loading="loadingProfiles"
                                            density="compact" item-title="label" item-value="id"
                                            label="Load import preset" variant="outlined" clearable hide-details
                                            @update:modelValue="applyProfile"/>
                        </v-col>
                        <v-col cols="12" sm="6" class="d-flex align-center">
                            <v-text-field v-model="newProfileName" density="compact" hide-details
                                          label="Preset name to save" variant="outlined" class="me-2"/>
                            <v-btn :disabled="!canSaveProfile || savingProfile" :loading="savingProfile"
                                   color="primary" size="small" variant="tonal"
                                   @click="saveProfile">Save
                            </v-btn>
                            <v-btn v-if="selectedProfileId" :loading="savingProfile" class="ms-2"
                                   color="error" icon="mdi-delete" size="small" variant="tonal"
                                   @click="deleteProfile"/>
                        </v-col>
                    </v-row>

                    <!-- Mapping grid --------------------------------------------------------- -->
                    <div v-if="fileHeader.length" class="mt-3">
                        <v-divider class="mb-3"/>
                        <h5 class="mb-2">Column mapping</h5>
                        <div class="text-caption text-grey-darken-1 mb-3">
                            Map each device field to a column in your CSV. Required fields are marked with *.
                            Auto-detected from the header on upload; loaded profile mappings take precedence.
                        </div>
                        <v-row dense>
                            <v-col v-for="field in knownFields" :key="field.key" cols="12" md="6">
                                <v-select v-model="formData.mapping[field.key]"
                                          :items="fileHeaderOptions"
                                          :label="field.label + (field.required ? ' *' : '')"
                                          :rules="field.required ? [v => !!v || `${field.label} mapping is required`] : []"
                                          clearable density="compact" hide-details="auto" variant="outlined"/>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Result panel --------------------------------------------------------- -->
                    <v-divider v-if="report" class="my-3"/>

                    <div v-if="report" class="report-block">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <h5 class="ma-0">{{ report.dryrun ? 'Preview Result' : 'Import Result' }}</h5>
                            <v-chip v-if="report.dryrun" color="info" size="small" variant="tonal">DRY RUN</v-chip>
                        </div>

                        <v-row dense class="text-caption">
                            <v-col cols="2"><strong>Total:</strong> {{ report.total }}</v-col>
                            <v-col cols="2" class="text-success">
                                <strong>Valid:</strong> {{ report.valid }}
                            </v-col>
                            <v-col cols="2" class="text-error">
                                <strong>Invalid:</strong> {{ report.invalid }}
                            </v-col>
                            <v-col cols="3">
                                <strong>{{ report.dryrun ? 'Would create' : 'Created' }}:</strong>
                                {{ report.dryrun ? wouldCreate : (report.created ?? 0) }}
                            </v-col>
                            <v-col cols="3">
                                <strong>{{ report.dryrun ? 'Would update' : 'Updated' }}:</strong>
                                {{ report.dryrun ? wouldUpdate : (report.updated ?? 0) }}
                            </v-col>
                        </v-row>

                        <div v-if="reportRows.length" class="mt-3">
                            <small class="font-weight-bold">Failed rows:</small>
                            <v-table density="compact" class="mt-1 report-table">
                                <thead>
                                <tr>
                                    <th style="width: 60px;">Row</th>
                                    <th style="width: 160px;">slno</th>
                                    <th>Errors</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="entry in reportRows" :key="entry.row">
                                    <td>{{ entry.row }}</td>
                                    <td>{{ entry.slno || '' }}</td>
                                    <td>
                                        <div v-for="(err, i) in entry.errors" :key="i">{{ err }}</div>
                                    </td>
                                </tr>
                                </tbody>
                            </v-table>
                        </div>
                    </div>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">Close</v-btn>
                    <v-btn :disabled="!canSubmit || submitting"
                           :loading="submitting && lastSubmitMode === 'preview'"
                           color="grey-darken-1" size="small" type="button"
                           @click="submit(true)">Preview</v-btn>
                    <v-btn :disabled="!canSubmit || submitting"
                           :loading="submitting && lastSubmitMode === 'commit'"
                           color="primary" size="small" type="submit">Import</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>

    <v-dialog v-model="confirmDeleteOpen" width="400">
        <v-card>
            <v-card-title>Delete profile?</v-card-title>
            <v-card-text>This will remove the saved mapping profile. Imports already done are not affected.</v-card-text>
            <v-card-actions class="d-flex justify-end">
                <v-btn size="small" @click="confirmDeleteOpen = false">Cancel</v-btn>
                <v-btn color="error" size="small" variant="tonal" @click="confirmDeleteProfile">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
// read-excel-file v9 exports field only exposes /universal, /browser,
// /node, /web-worker. /browser would require ie11-style polyfills and
// returns a different shape under webpack; /universal works in modern
// browsers and returns rows as a plain array of arrays.
import readXlsxFile from "read-excel-file/universal";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
});
const emits = defineEmits(['update:modelValue', 'updateDevices']);

const knownFields = [
    {key: 'slno', label: 'slno', required: true},
    {key: 'aes_key', label: 'aes_key', required: true},
    {key: 'imei', label: 'imei'},
    {key: 'iccid', label: 'iccid'},
    {key: 'bt_mac', label: 'bt_mac'},
    {key: 'wifi_mac', label: 'wifi_mac'},
    {key: 'eth_mac', label: 'eth_mac'},
    {key: 'lora_mac', label: 'lora_mac'},
    {key: 'cpuid_1', label: 'cpuid_1'},
    {key: 'cpuid_2', label: 'cpuid_2'},
];
const knownKeys = knownFields.map(f => f.key);

const formData = reactive({
    valid: false,
    csv: null,
    product_sku_id: null,
    sim_card_type_id: null,
    portal_id: null,
    continue_on_error: false,
    overwrite_existing: false,
    mapping: {},
});

const dialogSwitch = ref(props.modelValue);
const errors = ref([]);
const skus = ref([]);
const loadingSkus = ref(false);
const simCardTypes = ref([]);
const loadingSimCardTypes = ref(false);
const portals = ref([]);
const loadingPortals = ref(false);
const submitting = ref(false);
const lastSubmitMode = ref(null);
const report = ref(null);

const fileHeader = ref([]);
const fileHeaderOptions = computed(() => fileHeader.value.map(h => ({title: h, value: h})));

const profiles = ref([]);
const loadingProfiles = ref(false);
const selectedProfileId = ref(null);
const newProfileName = ref('');
const savingProfile = ref(false);
const confirmDeleteOpen = ref(false);

const profileItems = computed(() =>
    profiles.value.map(p => {
        const sku = skus.value.find(s => s.id === p.product_sku_id);
        const skuTag = sku ? ` (${sku.label.split(' - ')[0]})` : '';
        return {id: p.id, label: `${p.name}${skuTag}`};
    })
);

const reportRows = computed(() => {
    if (!report.value?.failed_rows) return [];
    return Object.entries(report.value.failed_rows).map(([row, info]) => ({
        row,
        slno: info?.slno ?? '',
        errors: Array.isArray(info?.errors) ? info.errors : [String(info)],
    }));
});

// In dry-run we can't tell from valid-count alone how many would be creates
// vs updates. Server returns counts; for dry-run we trust the server's
// would-create / would-update if it sends them, otherwise show valid as a
// single combined number.
const wouldCreate = computed(() => report.value?.would_create ?? report.value?.valid ?? 0);
const wouldUpdate = computed(() => report.value?.would_update ?? 0);

const requiredMapped = computed(() =>
    knownFields.filter(f => f.required).every(f => !!formData.mapping[f.key])
);

const canSubmit = computed(() =>
    !!formData.csv && !!formData.product_sku_id && requiredMapped.value
);

const canSaveProfile = computed(() =>
    !!newProfileName.value.trim() && Object.keys(currentMappingPayload()).length > 0
);

function currentMappingPayload() {
    const out = {};
    for (const k of knownKeys) {
        if (formData.mapping[k]) out[k] = formData.mapping[k];
    }
    return out;
}

onMounted(async () => {
    loadSkus();
    loadProfiles();
    loadSimCardTypes();
    loadPortals();
});

async function loadPortals() {
    loadingPortals.value = true;
    try {
        const response = await send('get', 'admin/portals');
        const list = response.data?.data ?? [];
        portals.value = (Array.isArray(list) ? list : []).map(p => ({
            id: p.id,
            label: p.name,
        }));
    } catch (e) { /* ignore */ }
    finally { loadingPortals.value = false; }
}

async function loadSkus() {
    loadingSkus.value = true;
    try {
        const response = await send(
            'get',
            'product-skus?list=true&columns=product_skus.id,sku,product_name'
        );
        const list = response.data?.data?.product_skus ?? [];
        skus.value = (Array.isArray(list) ? list : []).map(s => ({
            id: s.id,
            label: s.product_name ? `${s.sku} - ${s.product_name}` : s.sku,
        }));
    } catch (e) { /* ignore */ }
    finally { loadingSkus.value = false; }
}

async function loadSimCardTypes() {
    loadingSimCardTypes.value = true;
    try {
        // Route lives under the admin/ prefix on the API.
        const response = await send('get', 'admin/sim-card-types');
        const list = response.data?.data ?? [];
        simCardTypes.value = (Array.isArray(list) ? list : []).map(t => ({
            id: t.id,
            label: t.short_name ? `${t.short_name} - ${t.vendor_name}` : t.vendor_name,
        }));
    } catch (e) { /* ignore */ }
    finally { loadingSimCardTypes.value = false; }
}

async function loadProfiles() {
    loadingProfiles.value = true;
    try {
        const response = await send('get', 'devices/import-profiles');
        const list = response.data?.data ?? [];
        profiles.value = Array.isArray(list) ? list : [];
    } catch (e) { /* ignore */ }
    finally { loadingProfiles.value = false; }
}

// Read the header from the picked file so the user can map columns. Handles
// both CSV (FileReader → parse first line) and XLSX (read-excel-file → first
// row of the active sheet). Auto-mapping never overwrites an existing
// mapping value, but if a previous mapping points at a column that does not
// exist in the new file we drop it first so autoMap can refill from the new
// header.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // mirrors the backend max:5120 KB cap

function onFilePicked(file) {
    fileHeader.value = [];
    // Vuetify 3 v-file-input may emit a single File or an array of files
    // depending on the version. Normalize to the first File.
    if (Array.isArray(file)) {
        file = file[0] || null;
    }
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
        snackbar.showSnackbar(`File is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB).`, 'error');
        formData.csv = null;
        return;
    }
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        readXlsxFile(file)
            .then(result => {
                // read-excel-file/universal returns an array of
                // {sheet: string, data: Row[]} objects. Pick the first sheet's
                // data. The package root entry would have given us Row[]
                // directly, but its exports field doesn't expose a webpack
                // browser path so we use /universal here.
                let dataRows;
                if (Array.isArray(result) && result.length > 0
                    && result[0] && typeof result[0] === 'object'
                    && Array.isArray(result[0].data)) {
                    dataRows = result[0].data;
                } else if (Array.isArray(result) && Array.isArray(result[0])) {
                    // Fallback if a future version returns rows directly.
                    dataRows = result;
                } else {
                    // eslint-disable-next-line no-console
                    console.log('XLSX unexpected shape:', result);
                    snackbar.showSnackbar('XLSX shape not recognized; see console.', 'error');
                    return;
                }
                if (dataRows.length === 0) {
                    snackbar.showSnackbar('XLSX file appears empty (no rows).', 'error');
                    return;
                }
                if (!Array.isArray(dataRows[0])) {
                    snackbar.showSnackbar(
                        `XLSX header row is not an array (got ${typeof dataRows[0]}).`,
                        'error'
                    );
                    return;
                }
                const cols = dataRows[0].map(c => (c == null ? '' : String(c).trim()));
                if (cols.every(c => c === '')) {
                    snackbar.showSnackbar('XLSX header row is empty.', 'error');
                    return;
                }
                applyHeader(cols);
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.error('XLSX parse failed:', err);
                const detail = err?.message ? `: ${err.message}` : '';
                snackbar.showSnackbar(`Could not parse XLSX header${detail}`, 'error');
            });
        return;
    }
    const reader = new FileReader();
    reader.onload = () => {
        const text = String(reader.result || '');
        const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
        const stripped = firstLine.replace(/^\uFEFF/, '');
        const cols = parseCsvLine(stripped).map(c => c.trim());
        applyHeader(cols);
    };
    reader.readAsText(file);
}

function applyHeader(cols) {
    fileHeader.value = cols;
    // Drop any stale mappings (from a prior file or a profile) whose source
    // column does not exist in the new header. autoMap will fill from here.
    for (const k of knownKeys) {
        const src = formData.mapping[k];
        if (src && !cols.includes(src)) {
            delete formData.mapping[k];
        }
    }
    autoMap(cols);
}

function parseCsvLine(line) {
    const out = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQ) {
            if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
            else if (ch === '"') { inQ = false; }
            else { cur += ch; }
        } else {
            if (ch === ',') { out.push(cur); cur = ''; }
            else if (ch === '"' && cur === '') { inQ = true; }
            else { cur += ch; }
        }
    }
    out.push(cur);
    return out;
}

// Best-effort: exact match wins, then "Attr: <field>" pattern (SFC export),
// then case-insensitive match. Never overwrites an already-mapped value.
// Returns the count of fields that got auto-mapped this call.
function autoMap(cols) {
    let mapped = 0;
    for (const f of knownFields) {
        if (formData.mapping[f.key]) continue;

        const exact = cols.find(c => c === f.key);
        if (exact) { formData.mapping[f.key] = exact; mapped++; continue; }

        const attr = cols.find(c => c.toLowerCase() === `attr: ${f.key}`.toLowerCase());
        if (attr) { formData.mapping[f.key] = attr; mapped++; continue; }

        const ci = cols.find(c => c.toLowerCase() === f.key.toLowerCase());
        if (ci) { formData.mapping[f.key] = ci; mapped++; }
    }
    if (mapped > 0) {
        snackbar.showSnackbar(`Auto-mapped ${mapped} column${mapped === 1 ? '' : 's'}.`);
    }
}

function applyProfile(id) {
    if (!id) return;
    const p = profiles.value.find(x => x.id === id);
    if (!p) return;
    if (p.product_sku_id) formData.product_sku_id = p.product_sku_id;
    // Restore the rest of the form too, not just the SKU and the mappings.
    // Portal is the one that matters: importing without it produces devices no
    // customer can ever activate, because activation matches on portal_id.
    // Profiles saved before these columns existed come back null/undefined,
    // so leave the current value alone rather than blanking it.
    if (p.portal_id != null) formData.portal_id = p.portal_id;
    if (p.sim_card_type_id != null) formData.sim_card_type_id = p.sim_card_type_id;
    if (p.continue_on_error != null) formData.continue_on_error = !!p.continue_on_error;
    if (p.overwrite_existing != null) formData.overwrite_existing = !!p.overwrite_existing;
    formData.mapping = {};
    for (const k of knownKeys) {
        if (p.mappings?.[k]) formData.mapping[k] = p.mappings[k];
    }
    newProfileName.value = p.name;
}

async function saveProfile() {
    if (!canSaveProfile.value) return;
    savingProfile.value = true;
    try {
        const payload = {
            name: newProfileName.value.trim(),
            product_sku_id: formData.product_sku_id ?? null,
            portal_id: formData.portal_id ?? null,
            sim_card_type_id: formData.sim_card_type_id ?? null,
            // Laravel's boolean rule rejects the strings "true"/"false", so send
            // real booleans. This is JSON, not the multipart body the import uses.
            continue_on_error: !!formData.continue_on_error,
            overwrite_existing: !!formData.overwrite_existing,
            mappings: currentMappingPayload(),
        };
        const response = await send('post', 'devices/import-profiles', payload);
        await loadProfiles();
        selectedProfileId.value = response.data?.data?.id ?? null;
        snackbar.showSnackbar(response.data?.message ?? 'Profile saved.');
    } catch (e) {
        snackbar.showSnackbar(e.response?.data?.message ?? 'Could not save profile.', 'error');
    } finally {
        savingProfile.value = false;
    }
}

function deleteProfile() {
    if (!selectedProfileId.value) return;
    confirmDeleteOpen.value = true;
}

async function confirmDeleteProfile() {
    confirmDeleteOpen.value = false;
    if (!selectedProfileId.value) return;
    savingProfile.value = true;
    try {
        await send('delete', `devices/import-profiles/${selectedProfileId.value}`);
        selectedProfileId.value = null;
        newProfileName.value = '';
        await loadProfiles();
        snackbar.showSnackbar('Profile deleted.');
    } catch (e) {
        snackbar.showSnackbar(e.response?.data?.message ?? 'Could not delete profile.', 'error');
    } finally {
        savingProfile.value = false;
    }
}

const submit = (dryrun) => {
    errors.value = [];
    report.value = null;
    submitting.value = true;
    lastSubmitMode.value = dryrun ? 'preview' : 'commit';

    const fd = new FormData();
    fd.append('csv', formData.csv);
    fd.append('product_sku_id', formData.product_sku_id);
    if (formData.sim_card_type_id) {
        fd.append('sim_card_type_id', formData.sim_card_type_id);
    }
    if (formData.portal_id) {
        fd.append('portal_id', formData.portal_id);
    }
    if (formData.continue_on_error) fd.append('continue_on_error', '1');
    if (formData.overwrite_existing) fd.append('overwrite_existing', '1');
    if (dryrun) fd.append('dryrun', '1');
    for (const [k, v] of Object.entries(currentMappingPayload())) {
        fd.append(`mapping[${k}]`, v);
    }

    send('post', 'devices/import', fd, {
        headers: {'Content-Type': 'multipart/form-data'}
    })
        .then((response) => {
            const payload = response.data?.data;
            if (payload && typeof payload === 'object' && 'total' in payload) {
                report.value = payload;
            }
            // If the xlsx had multiple sheets, fold a warning into the main
            // snackbar message so we don't fire two snackbars back-to-back.
            const meta = payload?.file_meta;
            let userMsg = response.data.message;
            if (meta?.sheet_count > 1) {
                userMsg += ` (Read sheet "${meta.active_sheet}"; the file has ${meta.sheet_count} sheets.)`;
            }
            snackbar.showSnackbar(userMsg);
            const created = payload?.created ?? 0;
            const updated = payload?.updated ?? 0;
            if (!dryrun && response.data.success && (created + updated) > 0) {
                emits('updateDevices');
                if ((payload?.invalid ?? 0) === 0) {
                    emits('update:modelValue', false);
                }
            }
        })
        .catch(function (errorResponse) {
            const data = errorResponse.response?.data;
            if (data?.errors) {
                Object.keys(data.errors).forEach((key) => {
                    errors.value.push(data.errors[key][0]);
                });
            }
            if (data?.message) {
                errors.value.push(data.message);
            }
            if (data?.data?.rows) {
                report.value = {
                    dryrun,
                    continue_on_error: formData.continue_on_error,
                    overwrite_existing: formData.overwrite_existing,
                    total: data.data.total ?? 0,
                    valid: data.data.valid ?? 0,
                    invalid: data.data.invalid ?? Object.keys(data.data.rows).length,
                    created: 0,
                    updated: 0,
                    failed_rows: data.data.rows,
                };
            }
            snackbar.showSnackbar(data?.message ?? 'Something went wrong', 'error');
            document.getElementById('heading')?.scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => {
            submitting.value = false;
            lastSubmitMode.value = null;
        });
}
</script>

<style scoped>
.report-table :deep(td) {
    vertical-align: top;
}
</style>
